import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Chip,
    Divider,
    Button,
    Stack,
    IconButton,
    Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HotelIcon from "@mui/icons-material/Hotel";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { getData, API_ENDPOINTS } from "../../services/apiService";

const containerStyle = {
    width: "100%",
    height: "calc(100vh - 260px)",
    minHeight: "480px",
    borderRadius: "12px",
    backgroundColor: "#f5f5f5",
};

const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };

// Must match id+libraries used by every other map component so the
// @react-google-maps/api Loader singleton is only created once per session.
const LIBRARIES = ["places", "drawing"];

// Dot icons for fleet trucks
const MARKER_ICONS = {
    moving: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
    slow: "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
    stationary: "http://maps.google.com/mapfiles/ms/icons/ltblue-dot.png",
    delayed: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
    overnight: "http://maps.google.com/mapfiles/ms/icons/purple-dot.png",
};

function getMarkerIcon(truck) {
    if (truck.is_delayed) return MARKER_ICONS.delayed;
    if (truck.is_overnight_halt) return MARKER_ICONS.overnight;
    if (truck.status === "Moving") return MARKER_ICONS.moving;
    if (truck.status === "Slow") return MARKER_ICONS.slow;
    return MARKER_ICONS.stationary;
}

function formatDateTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function exportToCSV(fleet) {
    const headers = [
        "Trip ID",
        "Truck ID",
        "Driver Name",
        "Driver Mobile",
        "Status",
        "Speed (km/h)",
        "Latitude",
        "Longitude",
        "Delayed",
        "Delay Hours",
        "Overnight Halt",
        "Planned Arrival",
        "Actual Start",
        "Last Updated",
    ];

    const rows = fleet.map((t) => [
        t.trip_id,
        t.truck_id,
        t.driver?.name || "",
        t.driver?.mobile_number || "",
        t.status,
        t.latest_location?.speed_kmh ?? "",
        t.latest_location?.latitude ?? "",
        t.latest_location?.longitude ?? "",
        t.is_delayed ? "Yes" : "No",
        t.delay_hours || 0,
        t.is_overnight_halt ? "Yes" : "No",
        formatDateTime(t.planned_arrival),
        formatDateTime(t.actual_start_time),
        formatDateTime(t.latest_location?.createdAt),
    ]);

    const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fleet_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

export default function AdminFleetMap() {
    const [fleetData, setFleetData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTruck, setSelectedTruck] = useState(null);
    const [mapTilesLoaded, setMapTilesLoaded] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const intervalRef = useRef(null);

    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: "AIzaSyDxgYINpPW-E6u-OEJRh1-NY8-SCEYFW_s",
        libraries: LIBRARIES,
    });

    const fetchFleet = useCallback(async (showLoader = false) => {
        if (showLoader) setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.adminFleet);
            if (res?.result) {
                setFleetData(res.result);
                setLastRefreshed(new Date());
            }
        } catch (err) {
            console.error("Failed to fetch admin fleet:", err);
        } finally {
            if (showLoader) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFleet(true);
        intervalRef.current = setInterval(() => fetchFleet(false), 15000); // TEST MODE: 15 sec (prod: 30000)
        return () => clearInterval(intervalRef.current);
    }, [fetchFleet]);

    const trucksWithLocation = (fleetData?.fleet || []).filter(
        (t) => t.latest_location?.latitude && t.latest_location?.longitude
    );

    // Summary stats
    const totalActive = fleetData?.total_active || 0;
    const delayedCount = fleetData?.delayed_count || 0;
    const overnightCount = fleetData?.overnight_halt_count || 0;

    return (
        <Box sx={{ p: 3 }}>
            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalShippingIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                        Live Fleet Map
                    </Typography>
                    {lastRefreshed && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            Updated {lastRefreshed.toLocaleTimeString("en-IN")}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Refresh now">
                        <IconButton onClick={() => fetchFleet(true)} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={() => exportToCSV(fleetData?.fleet || [])}
                        disabled={!fleetData?.fleet?.length}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {/* ── Summary KPI Row ── */}
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
                <Chip
                    icon={<LocalShippingIcon />}
                    label={`${totalActive} Active Trips`}
                    color="primary"
                    variant="outlined"
                />
                <Chip
                    icon={<WarningAmberIcon />}
                    label={`${delayedCount} Delayed`}
                    color={delayedCount > 0 ? "error" : "default"}
                    variant="outlined"
                />
                <Chip
                    icon={<HotelIcon />}
                    label={`${overnightCount} Overnight Halt`}
                    color={overnightCount > 0 ? "warning" : "default"}
                    variant="outlined"
                />
            </Stack>

            {/* ── Legend ── */}
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
                {[
                    { label: "Moving", color: "#4CAF50" },
                    { label: "Slow", color: "#FFC107" },
                    { label: "Stationary", color: "#64B5F6" },
                    { label: "Delayed", color: "#F44336" },
                    { label: "Overnight Halt", color: "#9C27B0" },
                ].map((l) => (
                    <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: l.color }} />
                        <Typography variant="caption">{l.label}</Typography>
                    </Box>
                ))}
            </Stack>

            {/* ── Map ── */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 480 }}>
                    <CircularProgress />
                </Box>
            ) : !isLoaded ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 480 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ position: "relative", borderRadius: "12px", overflow: "hidden" }}>
                    {!mapTilesLoaded && (
                        <Box
                            sx={{
                                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                                zIndex: 1, display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center",
                                bgcolor: "rgba(255,255,255,0.85)",
                            }}
                        >
                            <CircularProgress size={50} thickness={4} />
                            <Typography sx={{ mt: 2, color: "text.secondary" }}>Loading map tiles…</Typography>
                        </Box>
                    )}

                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={INDIA_CENTER}
                        zoom={5}
                        options={{
                            mapTypeControl: false,
                            streetViewControl: false,
                            fullscreenControl: true,
                        }}
                        onTilesLoaded={() => setMapTilesLoaded(true)}
                    >
                        {trucksWithLocation.map((truck) => (
                            <Marker
                                key={truck.trip_id}
                                position={{
                                    lat: parseFloat(truck.latest_location.latitude),
                                    lng: parseFloat(truck.latest_location.longitude),
                                }}
                                icon={getMarkerIcon(truck)}
                                onClick={() => setSelectedTruck(truck)}
                            />
                        ))}

                        {selectedTruck && selectedTruck.latest_location && (
                            <InfoWindow
                                position={{
                                    lat: parseFloat(selectedTruck.latest_location.latitude),
                                    lng: parseFloat(selectedTruck.latest_location.longitude),
                                }}
                                onCloseClick={() => setSelectedTruck(null)}
                            >
                                <TruckInfoCard truck={selectedTruck} />
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </Box>
            )}

            {/* ── No data state ── */}
            {!loading && totalActive === 0 && (
                <Box sx={{ textAlign: "center", mt: 4 }}>
                    <LocalShippingIcon sx={{ fontSize: 64, color: "text.disabled" }} />
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        No active trips right now
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

function TruckInfoCard({ truck }) {
    const loc = truck.latest_location;
    const driver = truck.driver;

    return (
        <Box sx={{ minWidth: 220, maxWidth: 270, p: 0.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                Trip #{truck.trip_id}
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Truck</Typography>
                <Typography variant="caption" fontWeight="medium">{truck.truck_id}</Typography>
            </Box>

            {driver && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Driver</Typography>
                    <Typography variant="caption" fontWeight="medium">{driver.name}</Typography>
                </Box>
            )}

            {driver?.mobile_number && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Mobile</Typography>
                    <Typography variant="caption">{driver.mobile_number}</Typography>
                </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip
                    label={truck.status}
                    size="small"
                    color={truck.status === "Moving" ? "success" : truck.status === "Slow" ? "warning" : "default"}
                    sx={{ height: 18, fontSize: 10 }}
                />
            </Box>

            {loc?.speed_kmh != null && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Speed</Typography>
                    <Typography variant="caption">{parseFloat(loc.speed_kmh).toFixed(1)} km/h</Typography>
                </Box>
            )}

            {truck.planned_arrival && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">ETA</Typography>
                    <Typography variant="caption">
                        {new Date(truck.planned_arrival).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                    </Typography>
                </Box>
            )}

            {truck.is_delayed && (
                <Chip
                    icon={<WarningAmberIcon style={{ fontSize: 14 }} />}
                    label={`Delayed ${truck.delay_hours}h`}
                    color="error"
                    size="small"
                    sx={{ mt: 0.5, width: "100%" }}
                />
            )}

            {truck.is_overnight_halt && (
                <Chip
                    icon={<HotelIcon style={{ fontSize: 14 }} />}
                    label="Overnight Halt"
                    color="warning"
                    size="small"
                    sx={{ mt: 0.5, width: "100%" }}
                />
            )}

            {loc?.createdAt && (
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
                    Last ping: {new Date(loc.createdAt).toLocaleTimeString("en-IN")}
                </Typography>
            )}
        </Box>
    );
}
